import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { createRestApi } from '~/App/Api/rest';
import VideoItem from './VideoItem';
import ListGroup from '~/App/Components/ListGroup';
import Pagination from 'react-js-pagination';
import { confirmAsync } from '~/App/Components/Modals/Confirm';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import colours from '~/utils/colours';
import { Recording } from '~/types/models';
import Button from '~/App/Components/Button';
import { faSpinner, faUpload } from '@fortawesome/pro-solid-svg-icons';
import Tooltip, { themes } from '~/App/Components/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Config from '~/Config';
import { FormControl } from 'react-bootstrap';
import { isVideoFile } from '~/utils/video';

const StyledPickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const StyledHeader = styled.h3`
  margin: 0;
`;
const StyledBody = styled.div`
  flex: 1;
  position: relative;
  overflow: auto;
`;
const StyledEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;

  h2 {
    margin-top: 0;
  }
  p {
    color: ${colours.greyDarkAlt3};
  }
`;

const StyledListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const StlyedListGroup = styled(ListGroup)`
  overflow-y: auto;
  flex: 1;
  margin-bottom: 0;
`;

const StyledPaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButtonWrapper = styled.div`
  margin-left: auto;
`;

const URL = '/api/v2/recordings';

export interface VideoLibraryPickerProps {
  onDeleted: (id: Recording) => void;
  onClick: (id: Recording) => void;
  selectedVideoIds: string[];
  onValidUploadFile: (file: File) => void;
  onInvalidUploadFile: (title: string, msg: string) => void;
}

const VideoLibraryPicker = ({
  onDeleted,
  onClick,
  selectedVideoIds,
  onInvalidUploadFile,
  onValidUploadFile,
}: VideoLibraryPickerProps) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(16);
  const [total, setTotal] = useState(0);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const api = useMemo(() => createRestApi<Recording>(URL), []);

  const {
    data: recordings,
    isLoading,
    refetch,
  } = useQuery(['recordings', { page, perPage }], () =>
    api.getAll({ page, per_page: perPage })
  );

  const deleteAction = (id: string) => api.delete(id);

  const queryClient = useQueryClient();

  const { mutateAsync: deleteRecordingMutation } = useMutation(deleteAction, {
    onSuccess: (_, id) => {
      queryClient.invalidateQueries('recordings');
      const recording = recordings?.data.find(
        (recording) => recording.id == id
      );

      if (page === recordings?.last_page && recordings?.data.length === 1) {
        setPage(page - 1);
      }
      refetch();

      onDeleted(recording as Recording);
    },
  });

  const handleDeleteItem = async (itemId: string) => {
    if (await confirmAsync('Are you sure you want to delete this recording?')) {
      deleteRecordingMutation(itemId);
    }
  };

  const handleItemClick = (id: string) => {
    const recording = recordings?.data.find((recording) => recording.id == id);
    onClick(recording as Recording);
  };

  const isVideoSelected = (id: string) =>
    selectedVideoIds?.includes(id) ?? false;

  useEffect(() => {
    // Prevent the pagination from flickering
    // when navigating to next page.
    if (recordings?.total !== undefined) {
      setTotal(recordings.total);
    }
  }, [recordings?.total]);

  const handleUploadVideoClicked = () => {
    if (fileRef.current) {
      fileRef.current.value = '';
    }
    fileRef?.current?.click();
  };

  const handleFileChange = (
    evt: React.ChangeEvent<HTMLInputElement & FormControl>
  ): void => {
    const fileSelected = evt.currentTarget.files?.[0];

    if (!fileSelected) return;

    if (!isVideoFile(fileSelected)) {
      onInvalidUploadFile(
        'Invalid Video File',
        'Please select a valid video file'
      );
      return;
    }

    if (fileSelected.size > Config.FILE_UPLOAD_MAX_SIZE_B) {
      onInvalidUploadFile(
        'File too large',
        `Please only upload files under ${Config.FILE_UPLOAD_MAX_SIZE_MB}MB`
      );
      return;
    }

    onValidUploadFile(fileSelected);
  };

  return (
    <StyledPickerWrapper>
      <StyledHeaderContainer>
        <StyledHeader>
          Choose a video
          {!isLoading || <FontAwesomeIcon spin icon={faSpinner} />}
        </StyledHeader>
        <StyledButtonWrapper>
          <Tooltip
            id={'upload-library-tooltip'}
            placement='bottom'
            body={`Max video size: ${Config.FILE_UPLOAD_MAX_SIZE_MB}MB. We only accept MP4 or WebM formats.`}
            opacity={1}
            theme={themes.black}
          >
            <Button
              onClick={() => handleUploadVideoClicked()}
              icon={faUpload}
              bsStyle='secondary'
            >
              Upload Video
            </Button>
          </Tooltip>
        </StyledButtonWrapper>
      </StyledHeaderContainer>
      <input
        ref={fileRef}
        onChange={handleFileChange}
        type='file'
        accept='video/*'
        style={{
          position: 'absolute',
          display: 'none',
        }}
      />
      <p>
        These videos are uploaded or Bonjoro Chrome extension recordings, saved
        in your My Library.{' '}
        <a
          rel='noreferrer'
          href='https://help.bonjoro.com/en/articles/3844869-how-to-use-the-chrome-extension'
          target='_blank'
        >
          Learn more
        </a>{' '}
        about your My Library.{' '}
      </p>
      <StyledBody>
        {total > 0 && (
          <StyledListWrapper>
            <StlyedListGroup>
              {!isLoading &&
                recordings &&
                recordings.data.map((item, index) => (
                  <VideoItem
                    active={isVideoSelected(item.id)}
                    id={item.id}
                    key={index}
                    videoUrl={item.video_url}
                    title={item.name}
                    dateCreated={item.created_at}
                    onDelete={handleDeleteItem}
                    onClick={handleItemClick}
                  />
                ))}
            </StlyedListGroup>
            <StyledPaginationWrapper>
              <Pagination
                itemsCountPerPage={perPage}
                activePage={page}
                totalItemsCount={total}
                onChange={setPage}
                pageRangeDisplayed={5}
              />
            </StyledPaginationWrapper>
          </StyledListWrapper>
        )}
        {recordings && !recordings.data.length && !isLoading && (
          <StyledEmpty>
            <div>
              <h2>No videos found</h2>
              <p>No Chrome videos recorded or uploaded yet</p>
            </div>
          </StyledEmpty>
        )}
      </StyledBody>
    </StyledPickerWrapper>
  );
};

export default VideoLibraryPicker;
